import {
  Component,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { ERRORS } from 'src/app/components/reports/reports.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import {
  LocationErrorReportRequest,
  LocationErrorReports,
} from 'src/app/core/models/reports/error-reports.model';
import { ErrorReportsService } from 'src/app/core/services/reports/error/error-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { Options } from '../../../shared/report-format/report-format.constants';
import {
  FILE_NAME,
  LOCATION_ERROR_CONSTS,
  LOCATION_ERROR_PDF_REPORT,
} from './location-error-report.constants';

@Component({
  selector: 'app-location-error-report',
  standalone: false,
  templateUrl: './location-error-report.component.html',
  styleUrl: './location-error-report.component.scss',
})
export class LocationErrorReportComponent implements OnInit {
  @ViewChild('pdfContainer', { read: ViewContainerRef })
  pdfContainer!: ViewContainerRef;

  locationErrorReportForm!: FormGroup;
  locationErrorReportResponse!: LocationErrorReports;

  currDate!: Date;
  minDate!: Date;
  customMaxDate!: Date;
  toDate!: Date;

  hasSubmitted = false;

  readonly BUTTON_TYPES = BUTTON_TYPES;
  readonly CONSTS = LOCATION_ERROR_CONSTS;
  readonly PDF_REPORT = LOCATION_ERROR_PDF_REPORT;

  private readonly commonUtils = inject(CommonUtils);
  private readonly reportUtils = inject(ReportsUtils);
  private readonly errorReportsService = inject(ErrorReportsService);
  private readonly userService = inject(UserService);
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef: MatDialogRef<LocationErrorReportComponent> =
    inject(MatDialogRef<LocationErrorReportComponent>);

  /**
   * Used to initialize the form and set the default values for date fields
   *
   * @memberof LocationErrorReportComponent
   */
  ngOnInit() {
    this.currDate = new Date();
    this.minDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() - 7,
    );
    this.customMaxDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate(),
    );
    this.toDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate(),
    );

    this.locationErrorReportForm = this.fb.nonNullable.group({
      fromDate: [this.toDate],
      toDate: [this.toDate],
      reportFormat: [Options.PDF],
    });
  }

  /**
   * Used to get Location Error Report
   *
   * @private
   * @param {string} locationCode
   * @param {string} groupCode
   * @memberof ReportsComponent
   */
  getLocationErrorReport() {
    this.hasSubmitted = true;
    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),
        mergeMap((user) => {
          const formValue = this.locationErrorReportForm.value;
          const locationCode = user?.location;
          const fromDate = this.commonUtils.getFormattedDate(
            formValue.fromDate,
          );
          const toDate = this.commonUtils.getFormattedDate(formValue.toDate);

          const request: LocationErrorReportRequest = {
            locationCode: locationCode || '',
            fromDate: fromDate,
            toDate: toDate,
          };
          return this.errorReportsService.getLocationErrorReport(request).pipe(
            take(1),
            map((data) => {
              const formatType =
                this.locationErrorReportForm.get('reportFormat')?.value;
              const locationError = data?.response?.locationError || [];
              this.locationErrorReportResponse = data;

              if (locationError.length) {
                if (formatType === Options.CSV) {
                  const customHeader = [
                    [
                      '',
                      '',
                      '',
                      '',
                      `${ERRORS.buttons[0].text.toUpperCase()}`,
                      '',
                      '',
                      '',
                      '',
                    ],
                  ];
                  this.reportUtils.exportCSVReport(
                    locationError,
                    ERRORS.buttons[0].text,
                    customHeader,
                  );
                } else {
                  this.locationErrorReportResponse = data;
                  setTimeout(() => {
                    const content =
                      this.pdfContainer.element.nativeElement.innerHTML;
                    this.reportUtils.exportPdfReport(
                      content,
                      LOCATION_ERROR_PDF_REPORT.NAME,
                      FILE_NAME,
                    );
                  }, 0);
                }
                return this.dialogRef.close();
              } else {
                this.commonUtils.updateBannerType(
                  BANNER_TYPE.NO_DATA_AVAILABLE,
                );
                return this.dialogRef.close();
              }
            }),
          );
        }),
      )
      .subscribe();
  }
  /**
   * Used to set the report format value on radio change
   *
   * @param {Options} newValue
   * @memberof LocationErrorReportComponent
   */
  radioChange(newValue: Options) {
    this.locationErrorReportForm.get('reportFormat')?.setValue(newValue);
  }

  /**
   * Used to open exit modal on click of exit button
   *
   * @memberof LocationErrorReportComponent
   */
  clickExit() {
    this.commonUtils.openExitModal(
      !this.hasSubmitted && this.locationErrorReportForm.dirty,
    );
  }
}
